import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DataTable, { type ExpanderComponentProps, type TableColumn } from "react-data-table-component";
import styles from "./UnusedEntitlement.module.css";
import panel from "../admin/AdminPanel.module.css";
import adminStyles from "../admin/Admin.module.css";
import { adminTableTheme } from "../admin/adminTableTheme";
import { Modal } from "../admin/Modal";
import { TablePager } from "../shared/TablePager";
import { useColumnFilters, type ColumnFilterSpec } from "../shared/columnFilters";
import { useToast } from "../toast/toastContext";
import { formatDate } from "../consumption/formatters";
import { formatMonth } from "./formatMonth";
import { recordDormancyDecision, type UnusedEntitlementCandidateResponse, type UnusedEntitlementUnknownResponse } from "../../api/organisationReporting";

/** A person's name, or their email when no name is recorded, or their id when neither is known. */
function personLabel(row: { personName: string; personEmail: string; personId: string }): string {
  return row.personName.trim() || row.personEmail || row.personId;
}

/** A candidate row, with the key the table tells rows apart by. */
type CandidateRow = UnusedEntitlementCandidateResponse & { key: string };

/** An unknown row, with the key the table tells rows apart by. */
type UnknownRow = UnusedEntitlementUnknownResponse & { key: string };

/** The candidate whose seat is being given up, while the dialog asking for confirmation is open. */
type Confirming = { candidate: CandidateRow; note: string; pending: boolean } | null;

/**
 * The seats that could be given up (`SC-2`, `BR-03`, `BR-14`): everyone holding a seat, or allocated a tool, who has not used it for at
 * least the threshold, or who has left the tool's member list while still allocated it. A sortable, filterable, paged table; each row
 * opens to its evidence and the tool's caveat. Giving up a seat (`accounts.administer`) raises a request, on the person's behalf:
 * a request to switch the tool off when the portal allocates it, otherwise a manual task to remove the account. People who cannot be
 * assessed are listed apart, never counted as unused.
 *
 * @param props.candidates - Every candidate.
 * @param props.unknown - Every person who could not be assessed.
 * @param props.thresholdDays - The dormancy threshold.
 * @param props.canDecide - Whether the signed-in person may give up a seat.
 * @param props.exportAction - The candidates' CSV export button, for the panel's header.
 */
export function DormancyCandidatesSection({
  candidates,
  unknown,
  thresholdDays,
  canDecide,
  exportAction,
}: {
  candidates: UnusedEntitlementCandidateResponse[];
  unknown: UnusedEntitlementUnknownResponse[];
  thresholdDays: number;
  canDecide: boolean;
  exportAction?: React.ReactNode;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [rows, setRows] = useState<CandidateRow[]>(() => candidates.map((row) => ({ ...row, key: `${row.vendorId}-${row.personId}-${row.findingKind}` })));
  const [confirming, setConfirming] = useState<Confirming>(null);
  const unknownRows = useMemo<UnknownRow[]>(() => unknown.map((row) => ({ ...row, key: `${row.vendorId}-${row.personId}` })), [unknown]);

  const labels = useMemo(
    () => ({
      finding: (row: CandidateRow): string =>
        row.findingKind === "departed"
          ? t("unusedEntitlement.finding.departed")
          : row.lastUsedMonth === null
            ? t("unusedEntitlement.finding.neverUsed")
            : t("unusedEntitlement.finding.unused"),
      allocation: (row: CandidateRow): string => (row.allocated ? t("unusedEntitlement.allocation.yes") : t("unusedEntitlement.allocation.no")),
      decision: (row: CandidateRow): string =>
        row.existingDecision === "confirmed"
          ? t("unusedEntitlement.decision.requested")
          : row.existingDecision === "dismissed"
            ? t("unusedEntitlement.decision.dismissed")
            : t("unusedEntitlement.decision.open"),
    }),
    [t],
  );

  const filterSpecs = useMemo<ColumnFilterSpec<CandidateRow>[]>(
    () => [
      { id: "person", kind: "text", value: (row) => `${row.personName} ${row.personEmail}` },
      { id: "tool", kind: "set", value: (row) => row.vendorDisplayName },
      { id: "finding", kind: "set", value: labels.finding },
      { id: "allocation", kind: "set", value: labels.allocation },
      { id: "decision", kind: "set", value: labels.decision },
    ],
    [labels],
  );
  const { filtered, heading } = useColumnFilters(rows, filterSpecs);

  const columns = useMemo<TableColumn<CandidateRow>[]>(
    () => [
      {
        id: "person",
        name: heading("person", t("unusedEntitlement.table.person")),
        selector: (row) => personLabel(row).toLocaleLowerCase(),
        sortable: true,
        grow: 2,
        wrap: true,
        cell: (row) => (
          <span>
            <span className={styles.person}>{personLabel(row)}</span>
            {row.personName.trim() !== "" && row.personName.trim().toLocaleLowerCase() !== row.personEmail.toLocaleLowerCase() && <span className={styles.caveat}>{row.personEmail}</span>}
          </span>
        ),
      },
      { id: "tool", name: heading("tool", t("unusedEntitlement.table.vendor")), selector: (row) => row.vendorDisplayName, sortable: true },
      {
        id: "finding",
        name: heading("finding", t("unusedEntitlement.table.finding")),
        selector: (row) => row.lastUsedMonth ?? "",
        sortable: true,
        wrap: true,
        cell: (row) => (
          <span>
            {row.findingKind === "departed"
              ? t("unusedEntitlement.finding.departed")
              : row.lastUsedMonth
                ? t("unusedEntitlement.finding.lastUsed", { month: formatMonth(row.lastUsedMonth, i18n.language) })
                : t("unusedEntitlement.finding.neverUsed")}
            {row.findingKind === "departed" && row.disappearedFromMembersAt && (
              <span className={styles.caveat}>{t("unusedEntitlement.finding.departedSince", { date: formatDate(row.disappearedFromMembersAt.slice(0, 10)) })}</span>
            )}
          </span>
        ),
      },
      {
        id: "idle",
        name: t("unusedEntitlement.table.idle"),
        selector: (row) => row.daysSinceLastUse ?? -1,
        sortable: true,
        right: true,
        width: "8rem",
        format: (row) => (row.daysSinceLastUse === null ? "—" : t("unusedEntitlement.finding.days", { count: row.daysSinceLastUse })),
      },
      { id: "allocation", name: heading("allocation", t("unusedEntitlement.table.allocation")), selector: labels.allocation, sortable: true, width: "10rem" },
      {
        id: "decision",
        name: heading("decision", t("unusedEntitlement.table.decision")),
        selector: labels.decision,
        sortable: true,
        button: true,
        width: "11rem",
        cell: (row) =>
          row.existingDecision ? (
            <span className={row.existingDecision === "confirmed" ? styles.existingDecisionConfirmed : styles.existingDecisionDismissed}>{labels.decision(row)}</span>
          ) : canDecide ? (
            <button
              type="button"
              className={`button button-secondary ${styles.compactButton}`}
              aria-label={t("unusedEntitlement.decision.giveUpFor", { person: personLabel(row), tool: row.vendorDisplayName })}
              onClick={() => setConfirming({ candidate: row, note: "", pending: false })}
            >
              {t("unusedEntitlement.decision.giveUp")}
            </button>
          ) : (
            labels.decision(row)
          ),
      },
    ],
    [heading, labels, t, i18n.language, canDecide],
  );

  const confirm = async () => {
    if (confirming === null) {
      return;
    }
    const { candidate, note } = confirming;
    setConfirming({ ...confirming, pending: true });
    const result = await recordDormancyDecision(candidate.vendorId, candidate.personId, candidate.findingKind, "confirmed", note.trim() || undefined);
    if (result.kind === "ok") {
      setRows((current) =>
        current.map((row) =>
          row.key === candidate.key ? { ...row, existingDecision: "confirmed", existingDecisionNote: note.trim() || null, existingDecisionAt: new Date().toISOString() } : row,
        ),
      );
      setConfirming(null);
      toast.show(t("unusedEntitlement.decision.raised", { person: personLabel(candidate), tool: candidate.vendorDisplayName }), "success");
    } else if (result.kind === "aborted") {
      setConfirming(null);
    } else {
      setConfirming({ ...confirming, pending: false });
      toast.show(result.kind === "denied" ? t("unusedEntitlement.decisionDenied") : result.message, "error");
    }
  };

  const unknownSpecs = useMemo<ColumnFilterSpec<UnknownRow>[]>(
    () => [
      { id: "person", kind: "text", value: (row) => `${row.personName} ${row.personEmail}` },
      { id: "tool", kind: "set", value: (row) => row.vendorDisplayName },
    ],
    [],
  );
  const { filtered: unknownFiltered, heading: unknownHeading } = useColumnFilters(unknownRows, unknownSpecs);
  const unknownColumns = useMemo<TableColumn<UnknownRow>[]>(
    () => [
      {
        id: "person",
        name: unknownHeading("person", t("unusedEntitlement.table.person")),
        selector: (row) => personLabel(row).toLocaleLowerCase(),
        sortable: true,
        grow: 2,
        wrap: true,
        cell: (row) => (
          <span>
            {personLabel(row)}
            {row.personName.trim() !== "" && row.personName.trim().toLocaleLowerCase() !== row.personEmail.toLocaleLowerCase() && <span className={styles.caveat}>{row.personEmail}</span>}
          </span>
        ),
      },
      { id: "tool", name: unknownHeading("tool", t("unusedEntitlement.table.vendor")), selector: (row) => row.vendorDisplayName, sortable: true },
      { id: "reason", name: t("unusedEntitlement.table.reason"), selector: (row) => row.reason, grow: 4, wrap: true },
    ],
    [unknownHeading, t],
  );

  return (
    <>
      <section className={`${panel.panel} ${styles.firstPanel}`} aria-labelledby="dormancy-candidates-heading">
        <div className={panel.header}>
          <h2 id="dormancy-candidates-heading" className={`panel-title ${panel.title}`}>
            {t("unusedEntitlement.candidates.title")}
            <span className={styles.count}>{rows.length.toLocaleString(i18n.language)}</span>
          </h2>
          {exportAction}
        </div>
        <p className={panel.intro}>{t("unusedEntitlement.candidates.intro", { days: thresholdDays })}</p>
        {rows.length === 0 ? (
          <p className={styles.clear}>{t("unusedEntitlement.candidates.none")}</p>
        ) : (
          <div className={`${adminStyles.tableWrapper} ${adminStyles.peopleTableWrapper}`}>
            <DataTable
              persistTableHead
              columns={columns}
              data={filtered}
              className={adminStyles.peopleDataTable}
              keyField="key"
              ariaLabel={t("unusedEntitlement.candidates.title")}
              theme={adminTableTheme}
              defaultSortFieldId="idle"
              defaultSortAsc={false}
              pagination
              paginationComponent={TablePager}
              paginationPerPage={25}
              paginationRowsPerPageOptions={[25, 50, 100]}
              expandableRows
              expandableRowsComponent={CandidateEvidence}
              noDataComponent={<p className={adminStyles.note}>{t("unusedEntitlement.filters.noMatches")}</p>}
              striped
              highlightOnHover
              columnSeparator="subtle"
            />
          </div>
        )}
      </section>

      <section className={panel.panel} aria-labelledby="unknown-heading">
        <div className={panel.header}>
          <h2 id="unknown-heading" className={`panel-title ${panel.title}`}>
            {t("unusedEntitlement.unknown.title")}
            <span className={styles.count}>{unknown.length.toLocaleString(i18n.language)}</span>
          </h2>
        </div>
        <p className={panel.intro}>{t("unusedEntitlement.unknown.explanation", { days: thresholdDays })}</p>
        {unknown.length === 0 ? (
          <p className={styles.clear}>{t("unusedEntitlement.unknown.none")}</p>
        ) : (
          <div className={`${adminStyles.tableWrapper} ${adminStyles.peopleTableWrapper}`}>
            <DataTable
              persistTableHead
              columns={unknownColumns}
              data={unknownFiltered}
              className={adminStyles.peopleDataTable}
              keyField="key"
              ariaLabel={t("unusedEntitlement.unknown.title")}
              theme={adminTableTheme}
              defaultSortFieldId="tool"
              pagination
              paginationComponent={TablePager}
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10, 25, 50]}
              noDataComponent={<p className={adminStyles.note}>{t("unusedEntitlement.filters.noMatches")}</p>}
              striped
              highlightOnHover
              columnSeparator="subtle"
            />
          </div>
        )}
      </section>

      {confirming !== null && (
        <Modal title={t("unusedEntitlement.decision.dialogTitle", { person: personLabel(confirming.candidate), tool: confirming.candidate.vendorDisplayName })} onClose={() => setConfirming(null)}>
          <div className={styles.dialog}>
            <p>
              {confirming.candidate.allocated
                ? t("unusedEntitlement.decision.dialogAllocated", { tool: confirming.candidate.vendorDisplayName })
                : t("unusedEntitlement.decision.dialogManual", { tool: confirming.candidate.vendorDisplayName })}
            </p>
            <div className={adminStyles.field}>
              <label htmlFor="give-up-note">{t("unusedEntitlement.decision.noteLabel")}</label>
              <input
                id="give-up-note"
                type="text"
                value={confirming.note}
                disabled={confirming.pending}
                placeholder={t("unusedEntitlement.decision.notePlaceholder")}
                onChange={(event) => setConfirming({ ...confirming, note: event.target.value })}
              />
            </div>
            <div className={adminStyles.syncActions}>
              <button type="button" className="button button-secondary" disabled={confirming.pending} onClick={() => setConfirming(null)}>
                {t("unusedEntitlement.decision.cancel")}
              </button>
              <button type="button" className="button button-primary" data-autofocus disabled={confirming.pending} onClick={() => void confirm()}>
                {t("unusedEntitlement.decision.raise")}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

/** A candidate's evidence and the tool's caveat, under its row, with the note recorded when it was confirmed. */
function CandidateEvidence({ data }: ExpanderComponentProps<CandidateRow>): React.JSX.Element {
  return (
    <div className={styles.expanded}>
      <p>{data.evidence}</p>
      <p className={styles.caveat}>{data.caveat}</p>
      {data.existingDecisionNote && <p className={styles.caveat}>{data.existingDecisionNote}</p>}
    </div>
  );
}
